/**
 * The arithmetic behind a derived figure, opened from its Σ.
 *
 * A derived number is only as trustworthy as the numbers it was computed from,
 * so the question an analyst asks of `4.64x` is "out of what?" — and the answer
 * has to be readable: `net_debt / covenant_ebitda` is the server's key
 * language, not the ledger's. The popover writes the formula in the figures'
 * own LABELS and then lists, one line each, the value THIS run used for every
 * input. That makes the Σ a check the analyst can actually perform, instead of
 * a string of keys parked in the Source column.
 *
 * Keys that are not figures of this run print as themselves: a recipe can be
 * edited between runs, and a formula naming something the run never computed
 * should say so plainly rather than silently vanish.
 */

import React from "react";

import type { MonitoringFigure } from "../../types/monitoring";
import { NO_VALUE, formatFigure } from "./runFormat";

/** An identifier in a formula, or one of the operators we rewrite. */
const TOKEN = /[A-Za-z_][A-Za-z0-9_]*|[*/-]/g;

/** The keys a formula names, in the order it names them, without repeats. */
export function formulaKeys(formula: string): string[] {
	const keys: string[] = [];
	const pattern = /[A-Za-z_][A-Za-z0-9_]*/g;
	let match = pattern.exec(formula);
	while (match !== null) {
		if (!keys.includes(match[0])) keys.push(match[0]);
		match = pattern.exec(formula);
	}
	return keys;
}

/**
 * The formula as a person would write it: labels for keys, and the typographic
 * operators the rest of the ledger already uses (`−` is the same unicode minus
 * `formatDelta` signs its deltas with, so the two never look like different
 * characters on one screen).
 */
export function formulaInLabels(
	formula: string,
	labelOf: (key: string) => string | undefined,
): string {
	return formula.replace(TOKEN, (token) => {
		if (token === "*") return "×";
		if (token === "/") return "÷";
		if (token === "-") return "−";
		return labelOf(token) ?? token;
	});
}

export interface FormulaPopoverProps {
	/** The derived figure. Rendered only when it carries a formula. */
	figure: MonitoringFigure;
	/** Every figure in the run, so an input key can be given its label and value. */
	figuresByKey?: ReadonlyMap<string, MonitoringFigure>;
}

/**
 * The Σ affordance: a link-styled button that opens the formula beside it.
 *
 * It keeps the grey Σ glyph and its place after the value so the column's
 * alignment is unchanged, and it keeps the hover `title` for the analyst who
 * only wants the raw expression.
 */
const FormulaPopover: React.FC<FormulaPopoverProps> = ({
	figure,
	figuresByKey,
}) => {
	const [open, setOpen] = React.useState(false);
	const buttonRef = React.useRef<HTMLButtonElement>(null);
	const popoverRef = React.useRef<HTMLDivElement>(null);
	const formula = figure.formula ?? "";

	const close = React.useCallback(() => {
		setOpen(false);
		buttonRef.current?.focus();
	}, []);

	React.useEffect(() => {
		if (!open) return undefined;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};
		const onPointerDown = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (popoverRef.current?.contains(target)) return;
			if (buttonRef.current?.contains(target)) return;
			// Click-away is a dismissal, not a return: the pointer already moved
			// somewhere else, so stealing focus back would fight the user.
			setOpen(false);
		};
		document.addEventListener("keydown", onKeyDown);
		document.addEventListener("mousedown", onPointerDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.removeEventListener("mousedown", onPointerDown);
		};
	}, [open, close]);

	// `inputs` is the server's own answer and is authoritative; parsing the
	// formula is the fallback for a payload that predates it.
	const inputKeys =
		figure.inputs.length > 0 ? figure.inputs : formulaKeys(formula);
	const rows = inputKeys.map((key) => {
		const input = figuresByKey?.get(key);
		return {
			key,
			label: input?.label ?? key,
			text: input ? formatFigure(input.value, input.unit) : NO_VALUE,
		};
	});
	const written = formulaInLabels(formula, (key) => figuresByKey?.get(key)?.label);

	return (
		<span className="relative ml-1 inline-block align-baseline">
			<button
				type="button"
				ref={buttonRef}
				data-testid={`ledger-derived-${figure.key}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={`Formula for ${figure.label}`}
				title={formula}
				onClick={() => (open ? close() : setOpen(true))}
				className="text-gray-400 underline decoration-dotted underline-offset-2 hover:text-primary-600"
			>
				Σ
			</button>
			{open && (
				<div
					ref={popoverRef}
					role="dialog"
					aria-label={`Formula for ${figure.label}`}
					data-testid={`ledger-formula-${figure.key}`}
					className="absolute right-0 z-30 mt-1 w-64 rounded-md border border-gray-200 bg-white p-2 text-left shadow-lg"
				>
					<p className="text-sm font-medium text-text-strong">{written}</p>
					<ul className="mt-1.5 space-y-0.5">
						{rows.map((row) => (
							<li
								key={row.key}
								data-testid={`ledger-formula-input-${figure.key}-${row.key}`}
								className="flex items-baseline justify-between gap-3 text-sm text-text-muted"
							>
								<span>{row.label}</span>
								<span className="tabular-nums">{row.text}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</span>
	);
};

export default FormulaPopover;
